<script setup>
import OnboardingFeatureCard from './OnboardingFeatureCard.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';

const getters = useStoreGetters();
const { t } = useI18n();
const globalConfig = computed(() => getters['globalConfig/get'].value);
const currentUser = computed(() => getters.getCurrentUser.value);

const installationName = computed(() => globalConfig.value.installationName || 'ViperChat');

const greetingMessage = computed(() => {
  const hours = new Date().getHours();
  let translationKey;
  if (hours < 12) {
    translationKey = 'ONBOARDING.GREETING_MORNING';
  } else if (hours < 18) {
    translationKey = 'ONBOARDING.GREETING_AFTERNOON';
  } else {
    translationKey = 'ONBOARDING.GREETING_EVENING';
  }
  return t(translationKey, {
    name: currentUser.value.name,
    installationName: installationName.value,
  });
});
</script>

<template>
  <div
    class="min-h-screen lg:max-w-5xl max-w-4xl mx-auto grid grid-cols-2 grid-rows-[auto_1fr_1fr] auto-rows-min gap-4 p-8 w-full font-inter overflow-auto"
  >
    <!-- Header Section -->
    <div class="col-span-full self-start">
      <p
        class="text-xl font-semibold text-n-slate-12 font-interDisplay tracking-[0.3px]"
      >
        {{ greetingMessage }}
      </p>
      <p class="text-n-slate-11 max-w-2xl text-base">
        {{
          $t('ONBOARDING.DESCRIPTION', {
            installationName: installationName,
          })
        }}
      </p>
    </div>

    <!-- Card 1: Inbox -->
    <OnboardingFeatureCard
      image-src="/dashboard/images/onboarding/omnichannel-inbox.png"
      image-alt="Omnichannel"
      to="settings_inbox_new"
      :title="$t('ONBOARDING.ALL_CONVERSATION.TITLE')"
      :description="$t('ONBOARDING.ALL_CONVERSATION.DESCRIPTION')"
      :link-text="$t('ONBOARDING.ALL_CONVERSATION.NEW_LINK')"
    />

    <!-- Card 2: Team -->
    <OnboardingFeatureCard
      image-src="/dashboard/images/onboarding/teams.png"
      image-alt="Teams"
      to="settings_teams_new"
      :title="$t('ONBOARDING.TEAM_MEMBERS.TITLE')"
      :description="$t('ONBOARDING.TEAM_MEMBERS.DESCRIPTION')"
      :link-text="$t('ONBOARDING.TEAM_MEMBERS.NEW_LINK')"
    />

    <!-- Card 3: Kanban -->
    <div
      class="h-full w-full bg-n-background border border-n-weak rounded-lg p-4 flex flex-col"
    >
      <div class="flex-1 flex items-center justify-center">
        <div
          class="w-32 h-32 rounded-2xl flex items-center justify-center"
          style="background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 50%, #d946ef 100%)"
        >
          <span class="i-lucide-kanban w-16 h-16 text-white" />
        </div>
      </div>
      <div class="mt-auto">
        <p
          class="text-base text-n-slate-12 font-interDisplay font-semibold tracking-[0.3px]"
        >
          {{ $t('ONBOARDING.KANBAN.TITLE') }}
        </p>
        <p class="text-n-slate-11 text-sm">
          {{ $t('ONBOARDING.KANBAN.DESCRIPTION') }}
        </p>
        <p class="text-n-slate-10 text-xs mt-1 italic">
          {{ $t('ONBOARDING.KANBAN.REQUIRES_INBOX') }}
        </p>
      </div>
    </div>

    <!-- Card 4: AI Personalizada (replacing Labels) -->
    <div
      class="h-full w-full bg-n-background border border-n-weak rounded-lg p-4 flex flex-col"
    >
      <div class="flex-1 flex items-center justify-center">
        <div
          class="w-32 h-32 rounded-2xl flex items-center justify-center"
          style="background: linear-gradient(135deg, #14b8a6 0%, #10b981 50%, #22c55e 100%)"
        >
          <span class="i-lucide-bot w-16 h-16 text-white" />
        </div>
      </div>
      <div class="mt-auto">
        <p
          class="text-base text-n-slate-12 font-interDisplay font-semibold tracking-[0.3px]"
        >
          {{ $t('ONBOARDING.AI_ASSISTANT.TITLE') }}
        </p>
        <p class="text-n-slate-11 text-sm">
          {{ $t('ONBOARDING.AI_ASSISTANT.DESCRIPTION') }}
        </p>
        <router-link
          :to="{ name: 'omni_slots_config' }"
          class="no-underline text-n-brand text-sm font-medium"
        >
          <span>{{ $t('ONBOARDING.AI_ASSISTANT.NEW_LINK') }}</span>
          <span class="ml-2">{{ `→` }}</span>
        </router-link>
      </div>
    </div>
  </div>
</template>
